import React, { useState, useEffect, useRef } from 'react';
import { X, Hash, Send, Paperclip, Smile, MoreVertical, Pin, Bookmark, SquarePen, Trash2, Users, Image as ImageIcon, FileText, Reply, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { cn, getInitials } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { messageAPI, getChatWebSocketUrl } from '../lib/api';
import { toast } from 'sonner';
import EmojiPicker from 'emoji-picker-react';
import FileUploader from './discussion/FileUploader';
import FileAttachment from './discussion/FileAttachment';
import LinkPreview from './discussion/LinkPreview';
import ThreadPanel from './ThreadPanel';
import MentionInput from './discussion/MentionInput';
import { extractMentionUserIds } from '../lib/mentions';

export default function SpaceChannelSlideOver({ isOpen, onClose, channel, space }) {
    const { user } = useAuth();
    const { currentWorkspace, members: workspaceMembers } = useWorkspace();
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [ws, setWs] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [threadMessage, setThreadMessage] = useState(null); // For thread panel
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (isOpen && channel) {
            loadMessages();
            connectWebSocket();
            inputRef.current?.focus();
        }

        return () => {
            if (ws) {
                ws.close();
            }
        };
    }, [isOpen, channel]);

    const loadMessages = async () => {
        if (!channel) return;
        setLoading(true);
        try {
            const { data } = await messageAPI.getByChannel(channel.id);
            setMessages(data.messages || []);
            setTimeout(scrollToBottom, 100);
        } catch (error) {
            console.error('Failed to load messages:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoading(false);
        }
    };

    const connectWebSocket = () => {
        if (!channel || !user) return;

        const wsUrl = getChatWebSocketUrl(channel.id, user.id);
        const websocket = new WebSocket(wsUrl);

        websocket.onopen = () => {
            console.log('WebSocket connected');
        };

        websocket.onmessage = (event) => {
            const message = JSON.parse(event.data);
            if (message.type === 'new_message') {
                setMessages(prev => [...prev, message.data]);
                setTimeout(scrollToBottom, 100);
            } else if (message.type === 'message_updated') {
                setMessages(prev => prev.map(m => m.id === message.data.id ? message.data : m));
            } else if (message.type === 'message_deleted') {
                setMessages(prev => prev.filter(m => m.id !== message.data.message_id));
            }
        };

        websocket.onerror = (error) => {
            console.error('WebSocket error:', error);
        };

        websocket.onclose = () => {
            console.log('WebSocket disconnected');
        };

        setWs(websocket);
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() && attachments.length === 0) return;

        try {
            // Map attachments from upload response format to MessageAttachment schema
            const mappedAttachments = attachments.map((file) => ({
                id: file.id,
                filename: file.filename,
                file_url: file.file_url,
                file_type: file.content_type || file.file_type,
                file_size: file.file_size || file.size || 0,
                thumbnail_url: null,
            }));

            const response = await messageAPI.create(channel.id, {
                content: newMessage || '',
                message_type: 'text',
                attachments: mappedAttachments,
                mentions: extractMentionUserIds(newMessage || '', workspaceMembers || []),
                parent_id: null, // Main channel - no parent
            });
            
            // Optimistic UI update - add message immediately
            if (response.data) {
                setMessages(prev => [...prev, response.data]);
                setTimeout(scrollToBottom, 100);
            }
            
            setNewMessage('');
            setAttachments([]);
            setShowEmojiPicker(false);
        } catch (error) {
            console.error('Failed to send message:', error);
            
            let errorMsg = 'Failed to send message';
            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;
                if (Array.isArray(detail)) {
                    errorMsg = detail[0]?.msg || detail[0]?.message || errorMsg;
                } else if (typeof detail === 'string') {
                    errorMsg = detail;
                } else if (typeof detail === 'object') {
                    errorMsg = detail.msg || detail.message || errorMsg;
                }
            } else if (error.message) {
                errorMsg = error.message;
            }
            
            toast.error(String(errorMsg));
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleEmojiClick = (emojiData) => {
        setNewMessage(prev => prev + emojiData.emoji);
        setShowEmojiPicker(false);
    };

    const handleFileUpload = (uploadedFiles) => {
        setAttachments(prev => [...prev, ...uploadedFiles]);
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    if (!isOpen) return null;

    return (
        <>
            {/* Overlay */}
            <div 
                className="fixed inset-0 bg-black/50 z-40 transition-opacity"
                onClick={onClose}
            />

            {/* Slide Over Panel */}
            <div className="fixed inset-y-0 right-0 w-full sm:w-[500px] bg-white shadow-2xl z-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-white">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                            <Hash className="w-5 h-5 text-brand-primary" />
                            <div>
                                <h2 className="font-semibold text-lg">{channel?.name}</h2>
                                <p className="text-xs text-gray-500">{space?.name} Space</p>
                            </div>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose}>
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                {/* Messages Area */}
                <ScrollArea className="flex-1 p-4">
                    {loading ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                                <p className="text-sm text-gray-500">Loading messages...</p>
                            </div>
                        </div>
                    ) : messages.length === 0 ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="text-center">
                                <Hash className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-gray-500 font-medium">No messages yet</p>
                                <p className="text-sm text-gray-400 mt-1">Be the first to say something!</p>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {messages.map((message) => (
                                <MessageItem 
                                    key={message.id} 
                                    message={message} 
                                    currentUser={user}
                                    onOpenThread={() => setThreadMessage(message)}
                                />
                            ))}
                            <div ref={messagesEndRef} />
                        </div>
                    )}
                </ScrollArea>

                {/* Attachments Preview */}
                {attachments.length > 0 && (
                    <div className="px-4 py-3 border-t bg-gray-50">
                        <div className="flex flex-wrap gap-2">
                            {attachments.map((file, index) => (
                                <div 
                                    key={index} 
                                    className="inline-flex items-center gap-2 bg-white border border-gray-200 rounded-full px-3 py-1.5 text-xs hover:bg-gray-50 transition-colors group"
                                >
                                    {file.file_type?.startsWith('image/') ? (
                                        <ImageIcon className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                                    ) : (
                                        <FileText className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                                    )}
                                    <span className="truncate max-w-[150px] text-gray-700 font-medium">
                                        {file.filename}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => removeAttachment(index)}
                                        className="ml-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full p-0.5 transition-colors flex-shrink-0"
                                        title="Remove file"
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Input Area */}
                <div className="p-4 border-t bg-white">
                    <div className="flex items-end gap-2">
                        <div className="flex-1 relative">
                            <MentionInput
                                ref={inputRef}
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={`Kirim pesan ke #${channel?.name} (ketik @ untuk mention)`}
                                className="pr-20 min-h-[44px] max-h-[120px]"
                                members={workspaceMembers || []}
                                rows={1}
                            />
                            <div className="absolute right-2 top-2 flex items-center gap-1">
                                <FileUploader onFileUploaded={handleFileUpload} channelId={channel?.id}>
                                    <Button variant="ghost" size="icon" className="h-8 w-8">
                                        <Paperclip className="w-4 h-4" />
                                    </Button>
                                </FileUploader>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8"
                                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                >
                                    <Smile className="w-4 h-4" />
                                </Button>
                            </div>
                            {showEmojiPicker && (
                                <div className="absolute bottom-12 right-0 z-10">
                                    <EmojiPicker onEmojiClick={handleEmojiClick} />
                                </div>
                            )}
                        </div>
                        <Button 
                            onClick={handleSendMessage} 
                            disabled={!newMessage.trim() && attachments.length === 0}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            data-testid="channel-send-btn"
                        >
                            <Send className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>
            
            {/* Thread Panel */}
            <ThreadPanel
                parentMessage={threadMessage}
                channelId={channel?.id}
                currentUser={user}
                members={workspaceMembers || []}
                isOpen={!!threadMessage}
                onClose={() => setThreadMessage(null)}
            />
        </>
    );
}

// Message Item Component
function MessageItem({ message, currentUser, onOpenThread }) {
    const hasReplies = message.reply_count > 0;

    return (
        <div className={cn("group hover:bg-gray-50 p-2 rounded-lg transition-colors")}>
            <div className="flex gap-3">
                {/* Avatar */}
                <div className="flex-shrink-0">
                    {message.user?.avatar ? (
                        <img 
                            src={message.user.avatar} 
                            alt={message.user.name}
                            className="w-8 h-8 rounded-full"
                        />
                    ) : (
                        <div className="w-8 h-8 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs font-medium">
                            {getInitials(message.user?.name || 'U')}
                        </div>
                    )}
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{message.user?.name || 'Unknown'}</span>
                        <span className="text-xs text-gray-400">
                            {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    </div>
                    
                    <div className="text-sm text-gray-900">
                        {message.content}
                    </div>

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {message.attachments.map((attachment, index) => (
                                <FileAttachment key={index} attachment={attachment} />
                            ))}
                        </div>
                    )}

                    {/* Link Previews */}
                    {message.link_previews && message.link_previews.length > 0 && (
                        <div className="mt-2 space-y-2">
                            {message.link_previews.map((preview, index) => (
                                <LinkPreview key={index} preview={preview} />
                            ))}
                        </div>
                    )}

                    {/* Thread Count & Reply Button - Clickable */}
                    <div className="flex items-center gap-3 mt-2">
                        {hasReplies && (
                            <button
                                onClick={() => onOpenThread && onOpenThread()}
                                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                            >
                                <MessageSquare className="w-3 h-3" />
                                <span>{message.reply_count} {message.reply_count === 1 ? 'reply' : 'replies'}</span>
                            </button>
                        )}
                        
                        <button
                            onClick={() => onOpenThread && onOpenThread()}
                            className="flex items-center gap-1 text-xs text-gray-500 hover:text-brand-primary transition-colors opacity-0 group-hover:opacity-100"
                        >
                            <Reply className="w-3 h-3" />
                            <span>Reply in thread</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
